import { useClickAway } from '@ipfabric/ui-components/hooks/useClickAway';
import type { InteractiveAtomSize } from '@ipfabric/ui-components/utils/InteractiveAtomProps';
import { useOptionallyControlledState } from '@ipfabric/ui-components/utils/react/useOptionallyControlledState';
import classNames from 'classnames';
import { produce } from 'immer';
import { useState } from 'react';
import type { ComponentType, FC, ReactNode, RefObject } from 'react';
import deepEqual from 'react-fast-compare';
import {
  DropdownMenu,
  type DropdownMenuProps,
  type MenuPosition,
  defaultMenuPosition,
} from './DropdownMenu';
import {
  OverflowMenuButton,
  type OverflowMenuButtonProps,
} from './OverflowMenuButton';

interface UseOverflowMenuProps {
  container?: HTMLElement | null;
  parentRef: RefObject<HTMLElement>;
  defaultMenuPosition?: MenuPosition;
}

const useOverflowMenu = (props: UseOverflowMenuProps) => {
  const defaultPosition = props.defaultMenuPosition ?? defaultMenuPosition;
  const [fixedPosition, setFixedPosition] =
    useState<MenuPosition>(defaultPosition);

  const refCallback = (element: HTMLElement | null) => {
    if (element === null) {
      return;
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
    const updatedPosition = produce(fixedPosition, (draft) => {
      const parentHeight = props.parentRef.current!.offsetHeight;
      const elementHeight = element.offsetHeight;

      const container =
        props.container ??
        props.parentRef.current?.closest('.overflow-menu__container');

      const containerBounds = container?.getBoundingClientRect() ?? {
        // when no container is provided, get viewport bounds
        width: window.innerWidth,
        height: window.innerHeight,
        left: 0,
        top: 0,
      };

      const parentBounds = props.parentRef.current!.getBoundingClientRect();

      if (
        containerBounds.top + containerBounds.height <
          parentBounds.top + parentHeight + elementHeight &&
        // render up only if dropdown fits so it can be scrolled in low container
        parentBounds.top - elementHeight > 0
      ) {
        draft[1] = 'up';
      } else if (containerBounds.top > parentBounds.top) {
        draft[1] = 'down';
      } else {
        draft[1] = defaultPosition[1];
      }

      const parentWidth = props.parentRef.current!.offsetWidth;
      const elementWidth = element.offsetWidth;

      if (
        containerBounds.left + containerBounds.width <
          parentBounds.left + parentWidth + elementWidth &&
        // if left doesn't fit, prefer overflowing right
        parentBounds.left - elementWidth > 0
      ) {
        draft[0] = 'left';
      } else if (containerBounds.left > parentBounds.left) {
        draft[0] = 'right';
      } else {
        draft[0] = defaultPosition[0];
      }
    });

    if (!deepEqual(fixedPosition, updatedPosition)) {
      setFixedPosition(updatedPosition);
    }
  };

  return {
    menuRef: refCallback,
    position: fixedPosition,
  };
};

type Props = Omit<DropdownMenuProps, 'position' | 'onClose'> &
  Omit<OverflowMenuButtonProps, 'onClick' | 'children'> & {
    /**
     * Pass in a container element to ensure the context menu will be visible
     *
     * If no container is passed, the closest .overflow-menu__container ancestor
     * is looked up. Otherwise, window inner dimensions are used.
     *
     * When you open the context menu too close to right or bottom of that container,
     * it will do the best effort to reposition to other side to be visible.
     */
    container?: HTMLElement | null;
    dataTestId?: string;
    ButtonComponent?: ComponentType<OverflowMenuButtonProps>;
    buttonLabel?: ReactNode;
    defaultMenuPosition?: MenuPosition;
    size?: InteractiveAtomSize;
    open?: boolean;
    onSetOpen?: (open: boolean) => void;
  };

export const OverflowMenu: FC<Props> = (props) => {
  const [open, setOpen] = useOptionallyControlledState(
    props.open,
    props.onSetOpen,
    false,
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleBtnClick = () => {
    setOpen(!open);
  };

  const parentRef = useClickAway<HTMLDivElement>(() => {
    handleClose();
  });

  const { menuRef, position } = useOverflowMenu({
    container: props.container,
    parentRef,
    defaultMenuPosition: props.defaultMenuPosition,
  });

  const OverflowMenuButtonComponent =
    props.ButtonComponent ?? OverflowMenuButton;

  return (
    <div className='overflow-menu' ref={parentRef}>
      <OverflowMenuButtonComponent
        active={open}
        dataTestId={props.dataTestId}
        disabled={props.disabled}
        icon={props.icon}
        iconStyle={props.iconStyle}
        loading={props.loading}
        onClick={handleBtnClick}
        size={props.size}
      >
        {props.buttonLabel}
      </OverflowMenuButtonComponent>
      {open && (
        <DropdownMenu
          items={props.items}
          className={classNames(
            `ipf-dropdown-menu--${position[0]}`,
            `ipf-dropdown-menu--${position[1]}`,
          )}
          ref={menuRef}
          onClose={handleClose}
        />
      )}
    </div>
  );
};
